import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormButtons,
  FormHeader,
  Input,
  NewPasswordInputs,
  NormalizedException,
  useRsaContext,
} from "@songtradr-sso/shared/frontend";
import {
  Alert,
  Body,
  Button,
  ButtonLoadingIcon,
  Heading,
} from "@songtradr/design-language";
import { PageContainer } from "../../../components/page-container/PageContainer";
import {
  FormFields,
  routes,
  schema,
  setNewPassword,
  text,
  useLoaderData,
} from "./ConfirmPassword.Parts";

export const ConfirmPassword = () => {
  const navigate = useNavigate();
  const { rsaEncrypt } = useRsaContext();

  const { decryptEmail } = useLoaderData();

  const formMethods = useForm<FormFields>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    criteriaMode: "all",
    defaultValues: { [text.shared.inputs.email.id]: decryptEmail },
  });

  const {
    formState: { errors, isSubmitting },
    setError,
    watch,
  } = formMethods;

  const email = watch(text.shared.inputs.email.id);
  const errorMessage = !decryptEmail ? text.emailError : errors.root?.message;

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const rsaEncryptedRequest = await rsaEncrypt(
        JSON.stringify({
          email: data.email,
          password: data.newPassword,
          verificationCode: data.code,
        }),
      );
      const verified = await setNewPassword(rsaEncryptedRequest);
      if (verified) await navigate(routes.signIn);
    } catch (err) {
      setError("root", { message: new NormalizedException(err).message });
    }
  };

  return (
    <PageContainer floatTop backButton>
      <FormHeader>
        <Heading variant="h3-strong">{text.title}</Heading>
        <Body>{text.description}</Body>
      </FormHeader>
      <Form formMethods={formMethods} onSubmit={onSubmit}>
        <Heading variant="section">{text.inputLabel}</Heading>
        <Input
          id={text.shared.inputs.verificationCode.id}
          label={text.shared.inputs.verificationCode.label}
        />
        <NewPasswordInputs
          passwordLabel={text.shared.inputs.password.newPassword.label}
        />
        <Alert message={errorMessage} dismissible />
        <FormButtons>
          <Button
            $variant="important"
            type="submit"
            disabled={isSubmitting || !email}
          >
            {isSubmitting ? <ButtonLoadingIcon /> : text.button}
          </Button>
        </FormButtons>
      </Form>
    </PageContainer>
  );
};
